import ApiHelper from "../../../common/utils/apiHelper";
import {
  buildAirlineMilesAndMoreBookingFields,
  readAirlineMilesAndMoreNumber
} from "../../../common/utils/transport/airlineBookingPayload";
import { type TransportAirlineApiContext } from "../../../common/utils/transport/airlineTransportContext";
import {
  buildDestinationTransportPayload,
  postDestinationTransportEstimation
} from "../../../common/utils/transport/destinationTransportEstimation";
// helpers
import { isEmailInvalid } from "../../../common/utils/helpers/isEmailInvalid";
import { checkForEmptyField } from "../../../common/utils/helpers/checkForEmptyFields";
import { extractFullNameParts } from "../../../common/utils/helpers/extractFullNameParts";
// actions / selectors
import { globalActionCreators, selectApiBaseUrl } from "../../../common/store/modules/globalModule";
import { confirmationActionCreators } from "../../../common/store/modules/bookingConfirmationModule";
import {
  buildBookingUnitsPayload,
  buildDefaultBookingUnitsQuantities,
  buildEstimationBookingUnitsDetails
} from "./helpers/bookingUnits";
// types
import { type CatEstimation, type CatInitialResponse, type CatOffer } from "./types";

export const catActionTypes = {
  LOAD_CAT_INITIAL: "LOAD_CAT_INITIAL",
  TOGGLE_CAT_INITIAL_ERROR: "TOGGLE_CAT_INITIAL_ERROR",
  SET_SELECTED_OFFER: "SET_SELECTED_OFFER",
  SET_BOOKING_UNITS_QUANTITIES: "SET_BOOKING_UNITS_QUANTITIES",
  SET_DATA_FOR_BOOKING: "SET_DATA_FOR_BOOKING",
  SET_BOOKING_RESPONSE_DATA: "SET_BOOKING_RESPONSE_DATA",
  TOGGLE_BOOKING_ERROR: "TOGGLE_BOOKING_ERROR",
  SET_BOOKING_ERROR_MESSAGE: "SET_BOOKING_ERROR_MESSAGE",
  TOGGLE_CAT_PAYMENT_MODAL: "TOGGLE_CAT_PAYMENT_MODAL",
  SET_CAT_ESTIMATION: "SET_CAT_ESTIMATION",
  TOGGLE_CAT_ESTIMATION_ERROR: "TOGGLE_CAT_ESTIMATION_ERROR",
  RESET_CAT_STATE: "RESET_CAT_STATE",
  CAT_LAST_MILE_ROUTE_LOADING: "CAT_LAST_MILE_ROUTE_LOADING",
  CAT_LAST_MILE_ROUTE_SUCCESS: "CAT_LAST_MILE_ROUTE_SUCCESS",
  CAT_LAST_MILE_ROUTE_ERROR: "CAT_LAST_MILE_ROUTE_ERROR"
};

// Toggle mock for CAT offers endpoint
const USE_MOCK_CAT_OFFERS = false;

const MOCK_CAT_OFFERS_RESPONSE: CatInitialResponse = {
  provider_title: "City Airport Train (CAT)",
  provider_description:
    "The City Airport Train connects Vienna city center with Vienna International Airport in just 16 minutes. Book one-way or return tickets online.",
  provider_logo: {
    url: "https://d1o0qt6ld1319s.cloudfront.net/dev/zurich/390c0357-516c-45ae-91b6-152fb9996817/65249b87c14fafd.png",
    name: "CAT"
  },
  items: [
    {
      name: "CAT Ticket One Way",
      offer_code: "EDNBVRS",
      offer_prices: [],
      currency: "EUR",
      cancellation_policy: "Cancellation policy CAT one-way"
    },
    {
      name: "CAT Ticket Return",
      offer_code: "ETBLFHR",
      offer_prices: [
        { id: 3, price: 24.9, description: "Adult" },
        { id: 4, price: 12.5, description: "Child" }
      ],
      currency: "EUR",
      cancellation_policy: "Cancellation policy CAT return"
    }
  ]
};

export const initialState = {
  catInitial: null as CatInitialResponse,
  catInitialError: false,
  selectedOffer: null as CatOffer | null,
  bookingUnitsQuantities: {} as Record<number, number>,
  bookingResponseData: null as any,
  showPaymentModal: false,
  bookingError: false,
  bookingErrorMessage: [] as any[],
  catEstimation: null as CatEstimation | null,
  catEstimationError: false,
  dataForBooking: {
    commentForm: "",
    emailForm: "",
    nameForm: "",
    mobileNumberForm: ""
  },
  lastMileRoute: null as null | {
    distance_meters?: number | null;
    duration_seconds?: number | null;
    travel_mode?: string | null;
    google_maps_url?: string | null;
  },
  isLastMileRouteLoading: false,
  lastMileRouteError: null as string | null
};

const catModule = (state = initialState, action: any) => {
  switch (action.type) {
    case catActionTypes.LOAD_CAT_INITIAL:
      return {
        ...state,
        catInitial: action.catInitial
      };
    case catActionTypes.TOGGLE_CAT_INITIAL_ERROR:
      return {
        ...state,
        catInitialError: action.catInitialError
      };
    case catActionTypes.SET_SELECTED_OFFER:
      return {
        ...state,
        selectedOffer: action.selectedOffer
      };
    case catActionTypes.SET_BOOKING_UNITS_QUANTITIES:
      return {
        ...state,
        bookingUnitsQuantities: action.bookingUnitsQuantities
      };
    case catActionTypes.SET_DATA_FOR_BOOKING:
      return {
        ...state,
        dataForBooking: action.dataForBooking
      };
    case catActionTypes.SET_BOOKING_RESPONSE_DATA:
      return {
        ...state,
        bookingResponseData: action.bookingResponseData
      };
    case catActionTypes.TOGGLE_CAT_PAYMENT_MODAL:
      return {
        ...state,
        showPaymentModal: action.showPaymentModal
      };
    case catActionTypes.TOGGLE_BOOKING_ERROR:
      return {
        ...state,
        bookingError: action.bookingError
      };
    case catActionTypes.SET_BOOKING_ERROR_MESSAGE:
      return {
        ...state,
        bookingErrorMessage: action.bookingErrorMessage
      };
    case catActionTypes.SET_CAT_ESTIMATION:
      return {
        ...state,
        catEstimation: action.catEstimation
      };
    case catActionTypes.TOGGLE_CAT_ESTIMATION_ERROR:
      return {
        ...state,
        catEstimationError: action.catEstimationError
      };
    case catActionTypes.CAT_LAST_MILE_ROUTE_LOADING:
      return {
        ...state,
        isLastMileRouteLoading: Boolean(action.isLoading),
        ...(action.isLoading
          ? {
              lastMileRouteError: null
            }
          : {})
      };
    case catActionTypes.CAT_LAST_MILE_ROUTE_SUCCESS:
      return {
        ...state,
        isLastMileRouteLoading: false,
        lastMileRouteError: null,
        lastMileRoute: action.lastMileRoute ?? null
      };
    case catActionTypes.CAT_LAST_MILE_ROUTE_ERROR:
      return {
        ...state,
        isLastMileRouteLoading: false,
        lastMileRoute: null,
        lastMileRouteError: action.error ?? "transport.lastMileError"
      };
    case catActionTypes.RESET_CAT_STATE:
      return {
        ...state,
        ...initialState
      };
    default:
      return state;
  }
};

export default catModule;

type CatResolvedPaths =
  | { kind: "invalid_airline" }
  | {
      kind: "ok";
      offersPath: string;
      bookingsPath: string;
    };

export function resolveCatApiPaths(transportContext: TransportAirlineApiContext): CatResolvedPaths {
  if (transportContext.mode === "airline_missing_iata") {
    return { kind: "invalid_airline" };
  }
  if (transportContext.mode === "airline") {
    const base = `v1/airports/${encodeURIComponent(transportContext.iataCode)}/transports/cat`;
    return {
      kind: "ok",
      offersPath: base,
      bookingsPath: `v1/airports/${encodeURIComponent(transportContext.iataCode)}/bookings`
    };
  }
  // Hotel mode placeholder (resolved through this resolver; path can be updated server-side without changing callers)
  return { kind: "ok", offersPath: "transport/cat", bookingsPath: "bookings" };
}

export const catActionCreators = {
  resetCatStateAction: () => (dispatch: any) => {
    dispatch({ type: catActionTypes.RESET_CAT_STATE });
  },

  setSelectedOfferAction: (selectedOffer: CatOffer | null) => (dispatch: any) => {
    dispatch({ type: catActionTypes.SET_SELECTED_OFFER, selectedOffer });
  },

  setBookingUnitsQuantitiesAction: (bookingUnitsQuantities: Record<number, number>) => (dispatch: any) => {
    dispatch({ type: catActionTypes.SET_BOOKING_UNITS_QUANTITIES, bookingUnitsQuantities });
  },

  getCatEstimationAction:
    (
      params: {
        date: string;
        time: string;
        departureAddress?: string | null;
        departureAddressId?: string | null;
        destinationAddress?: string | null;
        destinationAddressId?: string | null;
      },
      transportContext: TransportAirlineApiContext
    ) =>
    async (dispatch: any, getState: any) => {
      const selectedOffer = getState().cat.selectedOffer as CatOffer | null;
      const bookingUnitsQuantities = getState().cat.bookingUnitsQuantities;
      const prices = selectedOffer?.offer_prices;
      const hasPrices = Array.isArray(prices) && prices.length > 0;

      dispatch({ type: catActionTypes.SET_CAT_ESTIMATION, catEstimation: null });
      dispatch({ type: catActionTypes.TOGGLE_CAT_ESTIMATION_ERROR, catEstimationError: false });

      if (!hasPrices) {
        return;
      }

      const paths = resolveCatApiPaths(transportContext);
      if (paths.kind === "invalid_airline") {
        dispatch({ type: catActionTypes.TOGGLE_CAT_ESTIMATION_ERROR, catEstimationError: true });
        return;
      }

      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));

      try {
        const apiUrl = selectApiBaseUrl(getState()) as string;
        const payload = buildDestinationTransportPayload({
          date: params.date,
          time: params.time,
          departureAddress: params.departureAddress ?? null,
          departureAddressId: params.departureAddressId ?? null,
          destinationAddress: params.destinationAddress ?? null,
          destinationAddressId: params.destinationAddressId ?? null,
          milesAndMore: readAirlineMilesAndMoreNumber(),
          offerCode: selectedOffer?.offer_code,
          details: buildEstimationBookingUnitsDetails(selectedOffer, bookingUnitsQuantities)
        });

        const data = await postDestinationTransportEstimation({
          apiUrl,
          transportContext,
          payload,
          v2: { path: "v2/transport/cat" },
          airline: {
            path: iataCode => `v1/airports/${encodeURIComponent(iataCode)}/transports/cat`
          }
        });

        dispatch({ type: catActionTypes.SET_CAT_ESTIMATION, catEstimation: data });
      } catch (_err: any) {
        dispatch({ type: catActionTypes.TOGGLE_CAT_ESTIMATION_ERROR, catEstimationError: true });
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    },

  getCatOffersAction: (transportContext: TransportAirlineApiContext) => async (dispatch: any, getState: any) => {
    const apiUrl = selectApiBaseUrl(getState());
    const paths = resolveCatApiPaths(transportContext);

    dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));
    dispatch({ type: catActionTypes.TOGGLE_CAT_INITIAL_ERROR, catInitialError: false });

    if (paths.kind === "invalid_airline") {
      dispatch({ type: catActionTypes.TOGGLE_CAT_INITIAL_ERROR, catInitialError: true });
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      return;
    }

    try {
      let data: CatInitialResponse;
      if (USE_MOCK_CAT_OFFERS) {
        await new Promise(resolve => setTimeout(resolve, 250));
        data = MOCK_CAT_OFFERS_RESPONSE;
      } else {
        const res = await ApiHelper.get(`${apiUrl}/${paths.offersPath}`);
        data = (res?.data ?? null) as CatInitialResponse;
      }

      dispatch({ type: catActionTypes.LOAD_CAT_INITIAL, catInitial: data });

      const first = (data as any)?.items?.[0] ?? null;
      dispatch(catActionCreators.setSelectedOfferAction(first));

      dispatch(catActionCreators.setBookingUnitsQuantitiesAction(buildDefaultBookingUnitsQuantities(first)));
    } catch (_err: any) {
      dispatch({ type: catActionTypes.TOGGLE_CAT_INITIAL_ERROR, catInitialError: true });
    } finally {
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
    }
  },

  updateFormForBookingAction:
    (dataForBooking: { commentForm: string; emailForm: string; nameForm: string; mobileNumberForm: string }) =>
    (dispatch: any) => {
      dispatch({ type: catActionTypes.SET_DATA_FOR_BOOKING, dataForBooking });
    },

  toggleBookingErrorAction: (bookingError: boolean) => (dispatch: any) => {
    dispatch({ type: catActionTypes.TOGGLE_BOOKING_ERROR, bookingError });
  },

  setBookingErrorMessageAction: (message: any) => (dispatch: any) => {
    dispatch({ type: catActionTypes.SET_BOOKING_ERROR_MESSAGE, bookingErrorMessage: message });
  },

  togglePaymentModalAction: (showPaymentModal: boolean) => (dispatch: any) => {
    dispatch({ type: catActionTypes.TOGGLE_CAT_PAYMENT_MODAL, showPaymentModal });
  },

  getCatLastMileRouteAction:
    ({
      departureLatitude,
      departureLongitude,
      destinationPlaceId,
      travelMode
    }: {
      departureLatitude: number;
      departureLongitude: number;
      destinationPlaceId: string;
      travelMode?: "walking" | "driving" | "transit" | "bicycling" | string;
    }) =>
    async (dispatch: any, getState: any) => {
      const apiUrl = selectApiBaseUrl(getState());
      const safePlaceId = typeof destinationPlaceId === "string" ? destinationPlaceId.trim() : "";

      if (!apiUrl || !safePlaceId) {
        return;
      }

      dispatch({
        type: catActionTypes.CAT_LAST_MILE_ROUTE_LOADING,
        isLoading: true
      });

      try {
        const query = [
          `departureLatitude=${encodeURIComponent(String(departureLatitude))}`,
          `departureLongitude=${encodeURIComponent(String(departureLongitude))}`,
          `destinationPlaceId=${encodeURIComponent(safePlaceId)}`,
          `travelMode=${encodeURIComponent(travelMode || "walking")}`
        ].join("&");
        const url = `${apiUrl}/v1/airports/last-mile-route?${query}`;
        const res = await ApiHelper.get(url);

        dispatch({
          type: catActionTypes.CAT_LAST_MILE_ROUTE_SUCCESS,
          lastMileRoute: res?.data ?? null
        });
        return res?.data;
      } catch (err: any) {
        dispatch({
          type: catActionTypes.CAT_LAST_MILE_ROUTE_ERROR,
          error: "transport.lastMileError"
        });
      } finally {
        dispatch({
          type: catActionTypes.CAT_LAST_MILE_ROUTE_LOADING,
          isLoading: false
        });
      }
    },

  buyTicketsAction:
    (
      params: { start_date: number; timeslot: string; offer_code: string | null },
      navigate: any,
      transportContext: TransportAirlineApiContext
    ) =>
    async (dispatch: any, getState: any) => {
      const dataForBooking = getState().cat.dataForBooking;
      const selectedOffer = getState().cat.selectedOffer;
      const bookingUnitsQuantities = getState().cat.bookingUnitsQuantities as Record<number, number>;
      const paths = resolveCatApiPaths(transportContext);

      const prices = selectedOffer?.offer_prices;
      const hasPrices = Array.isArray(prices) && prices.length > 0;
      if (!hasPrices) return;

      dispatch(catActionCreators.toggleBookingErrorAction(false));
      dispatch(catActionCreators.setBookingErrorMessageAction([]));

      let errorField = {
        nameForm: false,
        emailForm: false,
        mobileNumberForm: false,
        commentForm: false
      };

      let errorCheck = true;

      if (checkForEmptyField(dataForBooking.nameForm)) {
        errorField.nameForm = true;
        errorCheck = false;
      }
      if (isEmailInvalid(dataForBooking.emailForm) || checkForEmptyField(dataForBooking.emailForm)) {
        errorField.emailForm = true;
        errorCheck = false;
      }
      if (checkForEmptyField(dataForBooking.mobileNumberForm)) {
        errorField.mobileNumberForm = true;
        errorCheck = false;
      }
      if (dataForBooking?.commentForm !== "" && dataForBooking?.commentForm?.length < 3) {
        errorField.commentForm = true;
        errorCheck = false;
      }

      if (!errorCheck) {
        dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: true, errorField });
        return;
      }

      dispatch({ type: "TOGGLE_ERROR_FIELD", errorExists: false, errorField });
      dispatch(globalActionCreators?.toggleGlobalLoaderAction(true));

      localStorage.removeItem("bookingResponseDataStorage");

      const language = localStorage.getItem("i18nextLng") || "en";

      try {
        if (paths.kind === "invalid_airline") {
          dispatch(catActionCreators.toggleBookingErrorAction(true));
          return;
        }

        const apiUrl = selectApiBaseUrl(getState()) as string;

        const apiParams = {
          summary: {
            start_date: params?.start_date,
            timeslot: params?.timeslot,
            offer_code: params?.offer_code ?? selectedOffer?.offer_code ?? null,
            bookingUnits: buildBookingUnitsPayload(selectedOffer, bookingUnitsQuantities)
          },
          personal_information: {
            first_name: extractFullNameParts(dataForBooking.nameForm)?.firstName ?? null,
            last_name: extractFullNameParts(dataForBooking.nameForm)?.lastName ?? null,
            email: dataForBooking.emailForm,
            phone: dataForBooking.mobileNumberForm,
            comment: dataForBooking.commentForm ? dataForBooking.commentForm : null
          },
          ...buildAirlineMilesAndMoreBookingFields(transportContext),
          hotel_stay_code: getState().tenant.tenantInfo?.code
        };

        const response = await ApiHelper.post(`${apiUrl}/${paths.bookingsPath}`, {}, apiParams);
        const responseData = response?.data;

        localStorage.setItem("bookingResponseDataStorage", JSON.stringify(responseData));

        if (responseData?.booking_status === "error") {
          dispatch(catActionCreators.toggleBookingErrorAction(true));
          return;
        }

        dispatch(catActionCreators.toggleBookingErrorAction(false));
        dispatch({ type: catActionTypes.SET_BOOKING_RESPONSE_DATA, bookingResponseData: responseData });

        // if not paid, show PaymentModal; otherwise redirect to confirmation.
        if (responseData?.payment_status === "paid") {
          await dispatch(confirmationActionCreators?.getBookingStatusAction());
          await navigate(`/${language}/transport/cat/confirmation`, { replace: true });
        } else {
          dispatch(catActionCreators?.togglePaymentModalAction(true));
        }
      } catch (err: any) {
        if (err?.response?.data?.fields) {
          dispatch(catActionCreators.setBookingErrorMessageAction(err?.response?.data?.fields));
        }
        dispatch(catActionCreators.toggleBookingErrorAction(true));
      } finally {
        dispatch(globalActionCreators?.toggleGlobalLoaderAction(false));
      }
    }
};
